import {
  OPS_N1,
  billingAhead,
  eurK,
  monthShort,
  type BillingKind,
  type OpsChantier,
  type OpsN1Order,
} from '@cockpit/shared';
import { cn } from '@cockpit/ui';
import { useMemo, useState } from 'react';
import { plural } from '../../lib/format.js';

const STACK: { k: BillingKind; label: string; bar: string }[] = [
  { k: 'b', label: 'Planning Flex', bar: 'bg-azur-600' },
  { k: 'r', label: 'Chantiers en dérive', bar: 'bg-danger-600' },
  { k: 's', label: 'Démarrages à venir', bar: 'bg-warn-600' },
  { k: 'n', label: 'Commandes N-1 (GG/GA)', bar: 'bg-navy-300' },
];
const H = 160;
const monthName = (ym: string) => `${monthShort(ym.slice(0, 7))} ${ym.slice(0, 4)}`;

/** Facturation des douze prochains mois : planning Flex des chantiers en cours et commandes GG/GA de N-1. */
export function Billing({
  rows,
  n1,
  asOf,
  onOpen,
}: {
  rows: OpsChantier[];
  n1: OpsN1Order[];
  asOf: string;
  onOpen: (code: string) => void;
}) {
  const b = useMemo(() => billingAhead(rows, n1, asOf), [rows, n1, asOf]);
  const first = b.months.find((m) => m.total > 0)?.m ?? b.months[0]!.m;
  const [pick, setPick] = useState<string | null>(null);
  const sel = b.months.find((m) => m.m === pick) ?? b.months.find((m) => m.m === first)!;
  const max = Math.max(1, ...b.months.map((m) => m.total));
  const year = asOf.slice(0, 4);

  return (
    <section aria-labelledby="facturation" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="facturation" className="font-display text-[19px] font-bold text-ink">
          Facturation à venir
        </h2>
        <span className="text-[13px] text-ink-3">
          <b className="tnum text-ink">{eurK(b.raf)}</b> à facturer sur {plural(rows.length, 'chantier')},
          dont <b className="tnum text-ink">{eurK(b.fy)}</b> d’ici fin {year}
          {b.rafR > 0 && (
            <>
              {' '}
              · <span className="text-danger-700">{eurK(b.rafR)} sur des chantiers en dérive</span>
            </>
          )}
          {b.rafSansDate > 0 && <> · {eurK(b.rafSansDate)} sans date de facturation</>}
        </span>
      </div>
      <div className="grid items-start gap-4 rounded-md border border-line-soft bg-surface p-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-3">
          <div className="flex items-end gap-1.5 overflow-x-auto pb-1" role="group" aria-label="Mois">
            {b.months.map((m) => (
              <button
                key={m.m}
                type="button"
                aria-pressed={sel.m === m.m}
                aria-label={`${monthName(m.m)} : ${eurK(m.total)} à facturer`}
                onClick={() => setPick(m.m)}
                className={cn(
                  'flex min-w-[2.75rem] flex-1 flex-col items-center gap-1 rounded-sm border-2 px-0.5 py-1',
                  sel.m === m.m ? 'border-ink' : 'border-transparent hover:bg-surface-2',
                )}
              >
                <small className="tnum h-4 text-[11px] text-ink-3">
                  {m.total >= 500 ? eurK(m.total) : ''}
                </small>
                <span className="flex flex-col-reverse justify-start" style={{ height: H }}>
                  {STACK.map(({ k, bar }) => (
                    <i key={k} className={bar} style={{ height: (m[k] / max) * H, width: '1.5rem' }} />
                  ))}
                </span>
                <span className="text-[11.5px] text-ink-2">
                  {monthShort(m.m)}
                  {m.m.endsWith('-01') && m.m.slice(0, 4) !== year ? ` ${m.m.slice(2, 4)}` : ''}
                </span>
              </button>
            ))}
          </div>
          <ul className="flex flex-wrap gap-x-4 gap-y-1 text-[12.5px] text-ink-3">
            {STACK.map(({ k, label, bar }) => (
              <li key={k} className="flex items-center gap-1.5">
                <i aria-hidden className={cn('h-2.5 w-2.5 rounded-sm', bar)} /> {label}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col gap-1">
          <h3 className="font-display text-[16px] font-bold text-ink">
            {monthName(sel.m)} : {eurK(sel.total)} à facturer
          </h3>
          <p className="text-[12.5px] text-ink-3">
            {plural(sel.items.length, 'ligne')}
            {sel.items.length > 7 ? ', les 7 plus grosses' : ''}
          </p>
          {sel.items.length ? (
            sel.items.slice(0, 7).map((it, i) => {
              const inner = (
                <>
                  <span className="flex justify-between gap-3">
                    <b className="truncate text-[14px] text-ink">{it.label}</b>
                    <b className="tnum text-[14px]">{eurK(it.v)}</b>
                  </span>
                  <span className="truncate text-[12.5px] text-ink-3">
                    {it.code ?? 'sans affaire'} ·{' '}
                    {{ b: 'planning Flex', r: 'en dérive', s: 'démarrage à venir', n: 'commande N-1' }[it.k]}
                    {it.estimate ? ' · estimation (pas de ventilation Flex)' : ''}
                  </span>
                </>
              );
              const cls = 'flex w-full flex-col gap-0.5 border-t border-line-soft px-1 py-2 text-left';
              return it.code && it.k !== 'n' ? (
                <button
                  key={`${it.code}-${i}`}
                  type="button"
                  onClick={() => onOpen(it.code!)}
                  className={cn(cls, 'hover:bg-surface-2')}
                >
                  {inner}
                </button>
              ) : (
                <div key={`${it.code}-${i}`} className={cls}>
                  {inner}
                </div>
              );
            })
          ) : (
            <p className="text-[13.5px] text-ink-3">Rien de prévu ce mois-ci.</p>
          )}
        </div>
      </div>
      {b.n1.n > 0 && (
        <details className="rounded-md border border-line-soft bg-surface px-4 py-3 text-[13.5px]">
          <summary className="cursor-pointer font-semibold text-azur-700">
            Commandes GG/GA de N-1 retenues : {plural(b.n1.n, 'ligne')}, {eurK(b.n1.v)} commandés
          </summary>
          <p className="mt-2 text-ink-2">
            Hypothèses : {OPS_N1.f * 100} % facturés (soit {eurK(b.n1.f)}), {OPS_N1.a * 100} % d’achats et{' '}
            {OPS_N1.mo * 100} % de main-d’œuvre (soit {eurK(b.n1.c)} de coûts). Chaque commande est facturée
            douze mois après sa passation, au plus tôt ce mois-ci.
          </p>
          <ul className="mt-2 divide-y divide-line-soft">
            {n1.map((x, i) => (
              <li key={i} className="flex flex-wrap justify-between gap-2 py-1.5">
                <span className="min-w-0">
                  {x.ds || x.cli || x.code || 'Sans affaire'}
                  <small className="ml-2 text-ink-3">
                    {x.ent} · {x.type} · passée en {monthName(x.m0)} → facturée en {monthName(x.m)}
                  </small>
                </span>
                <b className="tnum">{eurK(x.v)}</b>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
